import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError, money } from "../lib/api";
import { competenciaFromInternal } from "../lib/competencia";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Barcode, QrCode, Copy, RefreshCw, MessageCircle, ShieldAlert, CalendarClock, ExternalLink } from "lucide-react";
import { alertaVencidaTexto, modalBloqueioCorpo } from "../lib/financeiro";
import PixChaveModal from "../components/PixChaveModal";
import { isRealInstrumentMode, platformProviderLabel } from "../lib/paymentProvider";

// Pacote consolidado pós-homologação, Prioridades 10/11/12/14/15 — área do
// PRÓPRIO tenant para pagar A MENSALIDADE do LOOTUS (a Assinatura SaaS da
// empresa junto à Altaforja/LOOTUS). NUNCA confundir com "Faturamento"
// (onde a empresa cobra os PRÓPRIOS clientes, item 20 do pedido) — mesmo
// que as duas telas mostrem "boletos/PIX", os dados/coleções/providers são
// inteiramente separados no backend (saas_charges vs invoices/boletos
// operacionais). Esta tela também é a "tela de acesso restrito" (item 14):
// quando a empresa está financeiramente bloqueada, ProtectedRoute manda
// qualquer navegação para cá — o mesmo conteúdo funciona nos dois casos,
// só com um aviso adicional no topo quando bloqueado.
const BOLETO_STATUS_LABEL = {
  waiting_payment: "Aguardando pagamento", processing: "Processando pagamento", paid: "Pago",
  cancelled: "Cancelado", expired: "Vencido", failed: "Rejeitado", unknown: "Status desconhecido",
};

const fmtDateTimeBr = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()} às ${hh}:${min}`;
};
const fmtDateBr = (v) => {
  if (!v) return "-";
  const [y, m, d] = String(v).slice(0, 10).split("-");
  if (!y) return v;
  return `${d}/${m}/${y}`;
};

const chargeStatusBadge = (c) => {
  if (c.status === "em_aberto" && c.vencimento && c.vencimento < new Date().toISOString().slice(0, 10)) {
    return <Badge className="bg-red-100 text-red-800">Vencida</Badge>;
  }
  // Finalização do Faturamento Master, Parte A — distinção "Aguardando
  // emissão" (título já existe, instrumento bancário ainda não) x "Em
  // aberto" (instrumento já gerado) é só de RÓTULO — nunca um valor novo
  // de status no backend, reaproveita boleto_gerado que já existe.
  if (c.status === "em_aberto" && !c.boleto_gerado) return <Badge className="bg-slate-100 text-slate-600">Aguardando emissão</Badge>;
  if (c.status === "em_aberto") return <Badge className="bg-cyan-100 text-cyan-800">Em aberto</Badge>;
  if (c.status === "pago" && c.baixa_origem === "manual") {
    return <Badge className="bg-amber-100 text-amber-800" title="Baixa manual administrativa">Pago — baixa manual</Badge>;
  }
  if (c.status === "pago") return <Badge className="bg-emerald-100 text-emerald-800">Pago</Badge>;
  return <Badge className="bg-slate-200 text-slate-700">Cancelado</Badge>;
};

const isChargeOverdue = (charge) => {
  if (!charge?.vencimento) return false;
  const venc = new Date(`${charge.vencimento}T00:00:00`);
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  return venc < hoje;
};

export default function Financeiro() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pixModalCharge, setPixModalCharge] = useState(null);
  // PIX por chave (mensalidade) — modalidade INDEPENDENTE do PIX integrado
  // (Mercado Pago) acima: usa sempre a chave "PIX da Altaforja" cadastrada
  // pelo Admin Master; o tenant nunca escolhe/configura nada aqui. A baixa
  // continua sendo exclusivamente o "Marcar como pago" do Admin Master.
  const [pixChaveModalCharge, setPixChaveModalCharge] = useState(null);
  const [regularizarTarget, setRegularizarTarget] = useState(null); // { charge, tipo }
  const [regularizarForm, setRegularizarForm] = useState({ novo_vencimento: "", instrumento_tipo: "boleto" });
  const [busyId, setBusyId] = useState(null);
  // Item 15 — modal mandatório de bloqueio: aparece uma vez ao ENTRAR em
  // Financeiro enquanto bloqueado (blockModalShownRef evita reabrir a cada
  // load() disparado por outras ações, ex.: sincronizar/gerar boleto).
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const blockModalShownRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: st }, { data: chs }] = await Promise.all([
        api.get("/financeiro/status"), api.get("/financeiro/charges"),
      ]);
      setStatus(st); setCharges(chs);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Correções de homologação (P2) — o AuthContext revalida user.financially_
  // blocked em segundo plano enquanto bloqueado (liberação por confiança/
  // baixa manual concedida pelo Admin Master em OUTRA sessão). Quando isso
  // muda para false enquanto o tenant está sentado nesta tela, recarrega o
  // status local também — sem isso, o banner/modal de bloqueio ficaria
  // "congelado" mostrando o estado antigo até o usuário navegar/atualizar.
  useEffect(() => { load(); }, [user?.financially_blocked, load]);

  // Item 14/15 — aparece UMA vez ao entrar em Financeiro enquanto bloqueado
  // (nunca reabre a cada load() disparado por outras ações desta tela).
  useEffect(() => {
    if (status?.bloqueado && !blockModalShownRef.current) {
      setBlockModalOpen(true);
      blockModalShownRef.current = true;
    }
  }, [status]);

  const copyToClipboard = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copiada`); }
    catch { toast.error("Não foi possível copiar — copie manualmente"); }
  };

  const openRegularizar = (charge, tipo) => {
    setRegularizarTarget({ charge, tipo });
    setRegularizarForm({ novo_vencimento: "", instrumento_tipo: tipo });
  };
  const submitRegularizar = async () => {
    if (!regularizarTarget) return;
    try {
      await api.post(`/financeiro/charges/${regularizarTarget.charge.id}/regularizar`, {
        novo_vencimento: regularizarForm.novo_vencimento || undefined,
        instrumento_tipo: regularizarForm.instrumento_tipo,
      });
      toast.success(regularizarForm.instrumento_tipo === "pix" ? "PIX gerado com sucesso" : "Boleto gerado com sucesso");
      setRegularizarTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generateInstrument = async (charge, tipo) => {
    if (!charge.boleto_gerado && isChargeOverdue(charge)) { openRegularizar(charge, tipo); return; }
    setBusyId(charge.id);
    try {
      const { data } = await api.post(`/financeiro/charges/${charge.id}/${tipo}`);
      toast.success(tipo === "pix" ? "PIX gerado com sucesso" : "Boleto gerado com sucesso");
      if (tipo === "pix") setPixModalCharge(data);
      load();
    } catch (e) {
      if (e?.response?.status === 400 && !charge.boleto_gerado) { openRegularizar(charge, tipo); return; }
      toast.error(formatApiError(e));
    } finally { setBusyId(null); }
  };

  const generatePixChave = async (charge) => {
    setBusyId(charge.id);
    try {
      const { data } = await api.post(`/financeiro/charges/${charge.id}/pix-chave`);
      toast.success("PIX por chave gerado");
      setPixChaveModalCharge(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const syncCharge = async (charge) => {
    setBusyId(charge.id);
    try {
      const { data } = await api.post(`/financeiro/charges/${charge.id}/sincronizar`);
      toast.success(`Situação sincronizada: ${BOLETO_STATUS_LABEL[data.boleto_status] || data.boleto_status || "atualizada"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  // Correções de homologação (P4) — o comprovante SEMPRE vai pelo WhatsApp
  // (o fluxo de upload interno foi removido). `charge` é opcional: quando
  // vem de uma linha específica da tabela, a mensagem referencia a
  // competência daquela cobrança (item 27/29 do pedido); o banner/modal
  // genéricos de bloqueio chamam sem cobrança específica.
  const openWhatsapp = async (charge) => {
    try {
      const { data } = await api.get("/financeiro/whatsapp-comprovante", { params: charge ? { charge_id: charge.id } : {} });
      window.open(data.wa_link, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bloqueado = !!status?.bloqueado;
  const trustedActive = !!status?.trusted_active;

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground">Financeiro</h1>
        <p className="text-sm text-muted-foreground">Pagamento da mensalidade LOOTUS da sua empresa — segunda via, boleto e PIX.</p>
      </div>

      {/* Prioridade 14 — tela de acesso restrito, quando bloqueado por
          pendência financeira (15 dias corridos de atraso sem pagamento
          confirmado). Prioridade 15 — botão de WhatsApp aqui dentro. */}
      {bloqueado && (
        <Card className="p-5 border-red-300 bg-red-50" data-testid="financeiro-bloqueado-banner">
          <div className="flex items-start gap-3">
            <ShieldAlert className="text-red-600 shrink-0 mt-0.5" size={22} />
            <div className="space-y-2">
              <div className="font-semibold text-red-900">Acesso temporariamente bloqueado por pendência financeira</div>
              <p className="text-sm text-red-800">Regularize sua mensalidade para restabelecer o acesso ao LOOTUS. Enquanto isso, esta área de Financeiro continua disponível para você pagar.</p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button variant="outline" className="bg-white" onClick={() => openWhatsapp()} data-testid="btn-whatsapp-comprovante">
                  <MessageCircle size={15} /> Enviar comprovante pelo WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
      {!bloqueado && trustedActive && (
        <Card className="p-4 border-blue-200 bg-blue-50" data-testid="financeiro-confianca-banner">
          <div className="flex items-center gap-2 text-sm text-blue-900">
            <CalendarClock size={16} />
            Acesso liberado temporariamente até <strong>{fmtDateTimeBr(status.trusted_access_until)}</strong>. A confirmação do pagamento ainda está pendente.
          </div>
        </Card>
      )}
      {/* Prioridade 12 — alertas discretos (vencida tem prioridade sobre
          disponível), nunca popups incessantes. */}
      {!bloqueado && status?.cobranca_vencida && (
        <Card className="p-4 border-amber-200 bg-amber-50" data-testid="financeiro-alerta-vencida">
          <div className="text-sm text-amber-900">{alertaVencidaTexto(status.dias_atraso)}</div>
        </Card>
      )}
      {!bloqueado && !status?.cobranca_vencida && status?.cobranca_disponivel && (
        <Card className="p-4 border-slate-200 bg-slate-50" data-testid="financeiro-alerta-disponivel">
          <div className="text-sm text-slate-700">
            Sua mensalidade LOOTUS deste mês já está disponível para pagamento por boleto ou PIX.
          </div>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Competência</th>
                <th className="text-left px-4 py-2">Vencimento</th>
                <th className="text-left px-4 py-2">Valor</th>
                <th className="text-left px-4 py-2">Situação</th>
                <th className="text-left px-4 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Carregando...</td></tr>)}
              {!loading && charges.length === 0 && (<tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Nenhuma mensalidade ainda.</td></tr>)}
              {charges.map((c) => {
                const vencExibido = c.provider_due_date || c.requested_due_date || c.vencimento;
                // Finalização do Faturamento Master, Parte F — antes só
                // reconhecia boleto_modo === "mercado_pago"; uma cobrança
                // gerada via Asaas (provedor principal) ficava travada nos
                // botões "Boleto"/"PIX (Mercado Pago)" para sempre, mesmo
                // com o instrumento real já emitido (boleto_url nunca
                // abria nada porque o fluxo nem chegava a esse ramo).
                const temInstrumento = c.boleto_gerado && isRealInstrumentMode(c.boleto_modo);
                const temPixChave = c.boleto_gerado && c.boleto_modo === "pix_chave";
                const providerLabel = platformProviderLabel(c.boleto_modo);
                return (
                  <tr key={c.id} className="border-t border-slate-100" data-testid={`financeiro-linha-${c.id}`}>
                    <td className="px-4 py-2">{competenciaFromInternal(c.competencia)}</td>
                    <td className="px-4 py-2">
                      {fmtDateBr(vencExibido)}
                      {/* Fundação Multi-Provider — causa raiz do "solicitado: -":
                          faltava exigir requested_due_date também, não só
                          provider_due_date !== requested_due_date. Em registro
                          legado sem requested_due_date, provider_due_date !==
                          undefined já era truthy e disparava o alerta com
                          "solicitado: -". Mesmo padrão já correto em
                          GestaoComercial.jsx. */}
                      {c.provider_due_date && c.requested_due_date && c.provider_due_date !== c.requested_due_date && (
                        <div className="text-[11px] text-amber-700">Ajustado pelo banco (solicitado: {fmtDateBr(c.requested_due_date)})</div>
                      )}
                    </td>
                    <td className="px-4 py-2 font-medium">{money(c.valor_final)}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1.5">
                        {chargeStatusBadge(c)}
                        {providerLabel && (
                          <Badge className="bg-slate-100 text-slate-600" title={`${c.instrumento_tipo === "pix" ? "PIX" : "Boleto"} real via ${providerLabel}`}>{providerLabel}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1.5">
                        {c.status === "em_aberto" && !temInstrumento && !temPixChave && (
                          <>
                            {/* Item 8/16 — emissão só é elegível a partir de
                                D-10; o botão continua visível (nunca some
                                silenciosamente), só fica desabilitado com um
                                aviso claro antes da janela abrir. PIX por
                                chave (item 27) é um recurso INDEPENDENTE,
                                nunca sujeito a esta regra. */}
                            {c.emissao_disponivel === false ? (
                              <span className="text-xs text-slate-500 self-center" title={`Boleto/PIX ainda não disponíveis`}>
                                Emissão disponível em {fmtDateBr(c.emissao_disponivel_em)}
                              </span>
                            ) : (
                              <>
                                <Button size="sm" variant="outline" disabled={busyId === c.id} onClick={() => generateInstrument(c, "boleto")} data-testid={`btn-boleto-${c.id}`}>
                                  <Barcode size={14} /> Boleto
                                </Button>
                                <Button size="sm" variant="outline" disabled={busyId === c.id} onClick={() => generateInstrument(c, "pix")} data-testid={`btn-pix-${c.id}`}>
                                  <QrCode size={14} /> PIX
                                </Button>
                              </>
                            )}
                            <Button size="sm" variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50" disabled={busyId === c.id} onClick={() => generatePixChave(c)} data-testid={`btn-pix-chave-${c.id}`}>
                              <QrCode size={14} /> PIX por chave
                            </Button>
                          </>
                        )}
                        {c.status === "em_aberto" && temPixChave && (
                          <Button size="sm" variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50" onClick={() => setPixChaveModalCharge(c)} data-testid={`btn-ver-pix-chave-${c.id}`}>
                            <QrCode size={14} /> Ver PIX
                          </Button>
                        )}
                        {c.status === "em_aberto" && temInstrumento && c.instrumento_tipo === "boleto" && (
                          <>
                            {c.boleto_url && (
                              <Button size="sm" variant="outline" onClick={() => window.open(c.boleto_url, "_blank", "noopener,noreferrer")} data-testid={`btn-abrir-boleto-${c.id}`}>
                                <ExternalLink size={14} /> Abrir boleto
                              </Button>
                            )}
                            {c.linha_digitavel && (
                              <Button size="sm" variant="outline" onClick={() => copyToClipboard(c.linha_digitavel, "Linha digitável")} data-testid={`btn-copiar-linha-${c.id}`}>
                                <Copy size={14} /> Copiar linha digitável
                              </Button>
                            )}
                          </>
                        )}
                        {c.status === "em_aberto" && temInstrumento && c.instrumento_tipo === "pix" && (
                          <Button size="sm" variant="outline" onClick={() => setPixModalCharge(c)} data-testid={`btn-ver-pix-${c.id}`}>
                            <QrCode size={14} /> Ver PIX
                          </Button>
                        )}
                        {c.status === "em_aberto" && temInstrumento && (
                          <Button size="sm" variant="ghost" disabled={busyId === c.id} onClick={() => syncCharge(c)} data-testid={`btn-sincronizar-${c.id}`}>
                            <RefreshCw size={14} /> Sincronizar
                          </Button>
                        )}
                        {c.status === "em_aberto" && (
                          <Button size="sm" variant="ghost" onClick={() => openWhatsapp(c)} data-testid={`btn-whatsapp-comprovante-${c.id}`}>
                            <MessageCircle size={14} /> Enviar comprovante pelo WhatsApp
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* PIX — QR Code + segurança (mesmo padrão da Gestão Comercial,
          Pacote consolidado pós-homologação, Prioridade 6). */}
      <Dialog open={!!pixModalCharge} onOpenChange={v => !v && setPixModalCharge(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>PIX — {competenciaFromInternal(pixModalCharge?.competencia)}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-center">
            {pixModalCharge?.pix_qr_code_base64 ? (
              <img src={`data:image/png;base64,${pixModalCharge.pix_qr_code_base64}`} alt="QR Code PIX" className="mx-auto w-48 h-48 border border-slate-200 rounded-md" />
            ) : (
              <p className="text-sm text-slate-500">QR Code indisponível — use o Copia e Cola abaixo.</p>
            )}
            <div className="text-left text-sm bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1">
              <div><span className="text-slate-500">Competência:</span> <span className="font-medium text-slate-900">{competenciaFromInternal(pixModalCharge?.competencia)}</span></div>
              <div><span className="text-slate-500">Valor:</span> <span className="font-medium text-slate-900">{pixModalCharge ? money(pixModalCharge.valor_final) : "-"}</span></div>
              <div><span className="text-slate-500">Expira em:</span> <span className="font-medium text-slate-900">{fmtDateTimeBr(pixModalCharge?.pix_expiration)}</span></div>
              <div><span className="text-slate-500">Descrição:</span> <span className="font-medium text-slate-900">Mensalidade LOOTUS — Competência {competenciaFromInternal(pixModalCharge?.competencia)}</span></div>
            </div>
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 text-left">
              Antes de pagar, confira se os dados do recebedor exibidos pelo seu banco correspondem à empresa responsável pela cobrança.
            </div>
            {pixModalCharge?.pix_qr_code && (
              <Button variant="outline" onClick={() => copyToClipboard(pixModalCharge.pix_qr_code, "PIX Copia e Cola")} data-testid="pix-copiar-copia-cola">
                <Copy size={14} /> Copiar PIX Copia e Cola
              </Button>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPixModalCharge(null)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIX por chave — modal SEPARADO do PIX Mercado Pago acima; nunca
          reaproveita pixModalCharge. Só copiar/baixar/compartilhar aqui —
          a baixa continua exclusiva do Admin Master ("Marcar como pago"). */}
      <PixChaveModal
        open={!!pixChaveModalCharge}
        onOpenChange={v => !v && setPixChaveModalCharge(null)}
        title={`PIX — ${competenciaFromInternal(pixChaveModalCharge?.competencia)}`}
        valor={pixChaveModalCharge?.valor_final}
        recebedor={pixChaveModalCharge?.pix_key_snapshot?.holder_name}
        instituicao={pixChaveModalCharge?.pix_key_snapshot?.institution_name}
        brcode={pixChaveModalCharge?.pix_brcode}
        onCopy={copyToClipboard}
      />

      {/* Regularizar cobrança vencida — Prioridade 11: encargos SEMPRE
          aplicados automaticamente, o cliente nunca pode desmarcar (ao
          contrário do fluxo do Admin Master em Gestão Comercial). */}
      <Dialog open={!!regularizarTarget} onOpenChange={v => !v && setRegularizarTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Regularizar mensalidade vencida</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              Esta mensalidade está vencida. Ao emitir um novo {regularizarTarget?.tipo === "pix" ? "PIX" : "boleto"}, multa e juros são
              aplicados automaticamente conforme a política comercial da LOOTUS (2% de multa + 1% ao mês, pro rata die).
            </p>
            <div>
              <Label>Novo vencimento (opcional — padrão: amanhã)</Label>
              <Input type="date" value={regularizarForm.novo_vencimento} onChange={e => setRegularizarForm(f => ({ ...f, novo_vencimento: e.target.value }))} data-testid="regularizar-novo-vencimento" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegularizarTarget(null)}>Cancelar</Button>
            <Button onClick={submitRegularizar} data-testid="regularizar-confirmar">Confirmar e gerar {regularizarTarget?.tipo === "pix" ? "PIX" : "boleto"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal mandatório de bloqueio (itens 15-17, atualizado no P4 —
          comprovante sempre pelo WhatsApp) — SEM "Fechar e continuar": só
          sai por uma das duas ações explícitas abaixo. Financeiro continua
          visível ao fundo (nunca esconde a tela). */}
      {status?.bloqueado && blockModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4" data-testid="financeiro-modal-bloqueio">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <ShieldAlert className="text-red-600 shrink-0 mt-0.5" size={24} />
              <h2 className="text-lg font-semibold text-slate-900">Acesso temporariamente suspenso</h2>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-line">{modalBloqueioCorpo(status.dias_atraso)}</p>
            <p className="text-xs text-slate-500">
              Se o pagamento já tiver sido realizado, envie o comprovante pelo WhatsApp para que nossa equipe possa verificar e agilizar a baixa.
              O WhatsApp será aberto com uma mensagem pronta. Anexe o comprovante antes de enviar.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={() => setBlockModalOpen(false)} data-testid="modal-bloqueio-ver-formas">Ver formas de pagamento</Button>
              <Button
                variant="outline"
                onClick={() => { setBlockModalOpen(false); openWhatsapp(status.cobranca_vencida); }}
                data-testid="modal-bloqueio-enviar-comprovante"
              >
                <MessageCircle size={14} /> Enviar comprovante pelo WhatsApp
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
